import type { Size } from '../geometry/framing';

/** A rectangle in output pixels. */
export interface PixelRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/**
 * Where a short-video app draws its own buttons and text over a vertical video,
 * as fractions of the frame. Approximate (apps change their layout now and
 * then) and on the careful side: a guide, not a promise.
 */
export interface PlatformOverlay {
  /** Top bar (tabs, search, live badge). */
  readonly top: number;
  /** Name, description, music and the comment box. */
  readonly bottom: number;
  /** A little room on the left edge. */
  readonly left: number;
  /** The like / comment / share column on the right… */
  readonly right: number;
  /** …which starts this far down. */
  readonly rightFrom: number;
}

export type ShortsPlatform = 'tiktok' | 'reels' | 'shorts';

export const PLATFORM_OVERLAYS: Readonly<Record<ShortsPlatform, PlatformOverlay>> = {
  tiktok: { top: 0.08, bottom: 0.2, left: 0.05, right: 0.13, rightFrom: 0.38 },
  reels: { top: 0.1, bottom: 0.22, left: 0.05, right: 0.12, rightFrom: 0.42 },
  shorts: { top: 0.08, bottom: 0.2, left: 0.05, right: 0.14, rightFrom: 0.4 },
};

/** Frames wider than this (width / height) aren't shown in the vertical feeds, so no guide. */
export const MAX_VERTICAL_ASPECT = 0.8;

/** Where every one of `platforms` leaves the picture uncovered: what to keep words inside of. */
export interface SafeArea {
  /** What the apps cover, in output pixels: top band, bottom band, left margin, right column. */
  readonly covered: readonly PixelRect[];
  /** The part none of them covers (inside the margins and above the bottom band). */
  readonly safe: PixelRect;
}

/** The overlays of several apps together: each edge as far in as the furthest of them. */
export function combinedOverlay(platforms: readonly ShortsPlatform[]): PlatformOverlay {
  const overlays = platforms.map((platform) => PLATFORM_OVERLAYS[platform]);
  const most = (pick: (overlay: PlatformOverlay) => number) => Math.max(0, ...overlays.map(pick));
  return { top: most((o) => o.top), bottom: most((o) => o.bottom), left: most((o) => o.left), right: most((o) => o.right), rightFrom: Math.min(1, ...overlays.map((o) => o.rightFrom)) };
}

/** The safe area of a vertical frame, or null when the frame isn't vertical (the feeds don't apply). */
export function safeAreaOf(output: Size, platforms: readonly ShortsPlatform[] = ['tiktok', 'reels', 'shorts']): SafeArea | null {
  const { width, height } = output;
  if (!(width > 0) || !(height > 0) || width / height > MAX_VERTICAL_ASPECT || platforms.length === 0) return null;
  const overlay = combinedOverlay(platforms);
  const top = overlay.top * height;
  const bottom = height - overlay.bottom * height;
  const left = overlay.left * width;
  const right = width - overlay.right * width;
  const columnTop = overlay.rightFrom * height;
  return {
    covered: [
      { x: 0, y: 0, w: width, h: top },
      { x: 0, y: bottom, w: width, h: height - bottom },
      { x: 0, y: top, w: left, h: bottom - top },
      { x: right, y: columnTop, w: width - right, h: bottom - columnTop },
    ],
    // Above the button column the right edge is free, but words there would run into it as they grow.
    safe: { x: left, y: top, w: right - left, h: bottom - top },
  };
}

const overlaps = (a: PixelRect, b: PixelRect): boolean => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

/** The covered parts `rect` runs into (empty when it sits fully in the clear). */
export const coveredBy = (area: SafeArea, rect: PixelRect): PixelRect[] => area.covered.filter((zone) => zone.w > 0 && zone.h > 0 && overlaps(zone, rect));
